import { FunctionComponent, useState } from 'react';
import { FlexContainer } from '@/components/Layout/Container';
import { styled, Typography } from '@mui/material';
import { LogoDropZone } from '@/components/LogoDropzone/LogoDropZone';
import { LoadingButton } from '@mui/lab';
import {
    useDeleteWebshopImageMutation,
    usePutWebshopImageMutation,
} from '@/api/v2/configurations';
import { useNotification } from '@starjack/features/notifications';
import { CreateWebshop, Webshop } from '@/models/webshop';

const ButtonContainer = styled(FlexContainer)(({ theme }) => ({
    width: '100%',
    marginTop: theme.spacing(3),
    alignItems: 'center',
    justifyContent: 'flex-end',
}));

type LogoUploadContainerProps = {
    label: string;
    subtitle?: React.ReactNode;
    imageUrl: string;
    webshop: Webshop | CreateWebshop;
    type: 'headerImage' | 'logo';
};

export const LogoUploadContainer: FunctionComponent<
    React.PropsWithChildren<LogoUploadContainerProps>
> = ({ label, imageUrl, webshop, type, subtitle }) => {
    const isLoading = false;
    const { addNotification } = useNotification();
    const [putLogo, { isLoading: uploadLoading }] =
        usePutWebshopImageMutation();
    const [deleteLogo, { isLoading: deleteLoading }] =
        useDeleteWebshopImageMutation();
    const [imageSrc, setImageSrc] = useState<'url' | 'upload'>(
        imageUrl ? 'url' : 'upload'
    );
    const [uploadState, setUploadState] = useState<
        'default' | 'uploaded' | 'saved'
    >('default');
    const [logo, setLogo] = useState<File>(null);

    const onSave = () => {
        const shop = webshop as Webshop;
        putLogo({ webshopId: shop.id, image: logo, type })
            .unwrap()
            .then(() => {
                setUploadState('saved');
                addNotification({
                    type: 'success',
                    message: 'Bild hochgeladen',
                });
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message: 'Fehler beim Hochladen',
                });
            });
    };

    const onDelete = () => {
        deleteLogo({ webshopId: (webshop as Webshop).id, type })
            .unwrap()
            .then(() => {
                setLogo(null);
                setImageSrc('upload');
                addNotification({
                    type: 'success',
                    message: 'Bild gelöscht',
                });
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message: 'Fehler beim Löschen',
                });
            });
    };

    return (
        <FlexContainer column fullWidth>
            <Typography
                sx={{
                    mb: 1,
                }}
            >
                {label}
            </Typography>

            {!!subtitle && (
                <FlexContainer
                    sx={{
                        ml: 2,
                        mb: 3,
                    }}
                    column
                >
                    {subtitle}
                </FlexContainer>
            )}

            <FlexContainer column fullWidth>
                {imageSrc === 'url' ? (
                    <>
                        <img
                            alt='Upload image'
                            style={{
                                width: '100%',
                                aspectRatio: '16 / 9',
                                objectFit: 'cover',
                            }}
                            src={imageUrl}
                        />
                        <ButtonContainer>
                            <LoadingButton
                                loading={deleteLoading}
                                onClick={onDelete}
                                color='error'
                                variant='contained'
                            >
                                Löschen
                            </LoadingButton>
                        </ButtonContainer>
                    </>
                ) : (
                    <>
                        <FlexContainer
                            sx={{
                                width: '100%',
                                height: '320px',
                            }}
                        >
                            <LogoDropZone
                                isLoading={isLoading}
                                onLogoChange={(logo) => {
                                    setUploadState('uploaded');
                                    setLogo(logo);
                                }}
                                logo={logo}
                            />
                        </FlexContainer>

                        {!!logo && (
                            <>
                                {uploadState === 'uploaded' && (
                                    <ButtonContainer>
                                        <LoadingButton
                                            sx={{
                                                mr: 2,
                                            }}
                                            onClick={() => {
                                                setLogo(null);
                                                setUploadState('default');
                                            }}
                                            variant='outlined'
                                        >
                                            Ändern
                                        </LoadingButton>
                                        <LoadingButton
                                            onClick={onSave}
                                            loading={uploadLoading}
                                            variant='contained'
                                        >
                                            Speichern
                                        </LoadingButton>
                                    </ButtonContainer>
                                )}

                                {uploadState === 'saved' && (
                                    <ButtonContainer>
                                        <LoadingButton
                                            loading={deleteLoading}
                                            onClick={onDelete}
                                            color='error'
                                            variant='contained'
                                        >
                                            Löschen
                                        </LoadingButton>
                                    </ButtonContainer>
                                )}
                            </>
                        )}
                    </>
                )}
            </FlexContainer>
        </FlexContainer>
    );
};
